import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getFileItems } from '../../../api'
import { EMPTY_LIST } from '../../../constants/common'
import { FILE_FOLDER_TYPE } from '../../../constants/file'
import { SIZE } from '../../../constants/size'
import {
  useInfiniteItemsListLoader,
  useInlineNotifications,
  useNotification,
  useOutboundUuid,
  useUserData,
} from '../../../hooks'
import { FileDirectoryBreadcrumbs, getFilesBreadcrumbs } from '../../File'
import { FormActions } from '../../Form'
import { List } from '../../List'
import { EmptyListItem } from '../../ListItem'
import Modal, { Box, Content, Header } from '../../Modal'
import { EMPTY_SEARCH_VALUE, SearchTextInput } from '../../Search'
import {
  getDocumentFolderFormErrorNotification,
  getDocumentFolderFormSuccessNotification,
  getDocumentFolderRoute,
} from '../DocumentFolderForm'
import { DocumentFolderFormModal } from '../DocumentFolderFormModal'

import { DOCUMENT_MOVE_TO_ACTION_MODAL_VALIDATION_FIELD_CAN_WRITE } from './DocumentMoveToActionModal.constants'
import { useDocumentMoveToActionModalValidation } from './DocumentMoveToActionModal.hooks'
import {
  getDocumentsDataItems,
  isValidValueForSearch,
} from './DocumentMoveToActionModal.utils'
import { FolderListItem } from './FolderListItem'

import * as styles from './DocumentMoveToActionModal.module.css'

function DocumentMoveToActionModal({
  initialNotifications,
  parent,
  submitProps,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation()

  const user = useUserData()

  const outboundUuid = useOutboundUuid()

  const [currentFolder, setCurrentFolder] = useState({ id: Number(parent) })
  const [count, setCount] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications({ initialNotifications })

  const [modal, setModal] = useState(null)
  const [searchValue, setSearchValue] = useState(EMPTY_SEARCH_VALUE)
  const [appliedSearch, setAppliedSearch] = useState(null)
  const [prevSearchValue, setPrevSearchValue] = useState(searchValue)

  // clears applied search when the query empties, during render
  if (searchValue !== prevSearchValue) {
    setPrevSearchValue(searchValue)

    if (!searchValue?.text) {
      setAppliedSearch(null)
    }
  }

  function addAdditionalData(data) {
    setCount(data.data.count)
    setCurrentFolder(data.data.parent)
  }

  const apiParams = {
    name: appliedSearch?.text,
    parent: currentFolder?.id,
    size: 10,
    type: FILE_FOLDER_TYPE,
  }

  async function getData(params) {
    return getFileItems(params, { outboundUuid })
  }

  const { getThresholdItemProps, items, loadingInitial, loadingMore } =
    useInfiniteItemsListLoader({
      apiParams,
      getData,
      getDataItems: getDocumentsDataItems,
      setAdditionalData: addAdditionalData,
      withPagination: true,
    })

  function handleSearch() {
    if (isValidValueForSearch(searchValue)) {
      setAppliedSearch(searchValue)
    }
  }

  function handleCloseAddNewModal() {
    setModal(null)
  }

  function handleErrorCreation() {
    handleAddErrorNotification(getDocumentFolderFormErrorNotification(t))
  }

  function handleSuccessCreation(data) {
    handleCloseAddNewModal()
    handleAddSuccessNotification(
      getDocumentFolderFormSuccessNotification(data, {
        t,
        to: getDocumentFolderRoute({ folderId: data.id, user }),
      })
    )
    setCurrentFolder(data)
  }

  function handleClickAddNew() {
    setModal(
      <DocumentFolderFormModal
        outboundUuid={outboundUuid}
        parent={currentFolder?.id}
        onClose={handleCloseAddNewModal}
        onError={handleErrorCreation}
        onSuccess={handleSuccessCreation}
      />
    )
  }

  const validateData = {
    [DOCUMENT_MOVE_TO_ACTION_MODAL_VALIDATION_FIELD_CAN_WRITE]:
      currentFolder?.permissions?.can_write,
    currentFolder,
  }

  const { isValidForm, notificationList, setValidationErrors } =
    useDocumentMoveToActionModalValidation({
      notifications,
      parent,
      validateData,
      onRemoveNotification: handleRemoveNotification,
      onSetNotification: handleAddNotification,
    })

  async function handleSubmit() {
    if (!isValidForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      await onSubmit({ values: { parent: currentFolder?.id } })
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleClickFolder(folder) {
    setValidationErrors(EMPTY_LIST)

    setCurrentFolder(folder)
  }

  const breadcrumbsItems = getFilesBreadcrumbs({
    directoryId: parent.id,
    handleClickOption: id => handleClickFolder({ id }),
    path: currentFolder?.path,
    t,
  })

  return (
    <Modal onClose={onClose}>
      <Box size={SIZE.LARGE}>
        <Header text={t('Documents.MoveTotModalTitleKey')} onClose={onClose} />
        {modal}
        <Content className={styles.content} size={SIZE.X_LARGE}>
          {notificationList}
          <List
            className={styles.list}
            isLoading={loadingInitial || loadingMore}
            itemsWrapperClassName={styles.folders}
            maxHeight={null}
          >
            <div className={styles.searchWrapper}>
              <SearchTextInput
                buttonProps={{
                  checkValueForSearch: isValidValueForSearch,
                }}
                value={searchValue}
                onChange={setSearchValue}
                onSearch={handleSearch}
              />
              <FileDirectoryBreadcrumbs
                count={count}
                items={breadcrumbsItems}
              />
            </div>
            {items?.length === 0 && !loadingInitial ? (
              <div className={styles.empty}>
                <EmptyListItem />
              </div>
            ) : null}

            {items?.map((item, index) => (
              <FolderListItem
                {...getThresholdItemProps(index)}
                item={item}
                key={index}
                onClick={handleClickFolder}
              />
            ))}
          </List>
        </Content>
        <FormActions
          {...(!currentFolder?.permissions?.can_write
            ? { ButtonAdd: false }
            : null)}
          ButtonClear={false}
          UploadComponent={false}
          addProps={{
            title: 'Documents.AddNewFolderBtnKey',
            onClick: handleClickAddNew,
          }}
          cancelProps={{
            onClick: onClose,
          }}
          className={styles.actions}
          submitProps={{
            ...submitProps,
            isLoading: isSubmitting || submitProps?.isLoading,
            title: submitProps?.title ?? 'Documents.MoveToFolderBtnKey',
            onClick: handleSubmit,
          }}
        />
      </Box>
    </Modal>
  )
}

export default DocumentMoveToActionModal
